import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Grid,
  Chip,
  Paper,
  Badge,
  Stack,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Description as ContractIcon,
  FolderOpen as DocumentIcon,
  Receipt as PayslipIcon,
  AccessTime as TimesheetIcon,
  Assignment as PostingIcon,
  ExitToApp as LogoutIcon,
  Notifications as NotificationIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Euro as EuroIcon
} from '@mui/icons-material';
import api from '../services/api';

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [stats, setStats] = useState({
    activeContract: null,
    documentsExpiring: 0,
    recentPayslips: 0,
    pendingTimesheets: 0,
    currentPosting: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loggedIn = localStorage.getItem('employeeLoggedIn');
    const data = localStorage.getItem('employeeData');
    
    if (!loggedIn || !data) {
      navigate('/employee/login');
      return;
    }
    
    setEmployeeData(JSON.parse(data));
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/employee/dashboard');
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('employeeLoggedIn');
    localStorage.removeItem('employeeData');
    navigate('/employee/login');
  };

  const menuItems = [
    { title: 'Dashboard', icon: <HomeIcon />, path: '/employee/dashboard' },
    { title: 'My Profile', icon: <PersonIcon />, path: '/employee/profile' },
    { title: 'Contracts', icon: <ContractIcon />, path: '/employee/contracts' },
    { title: 'Documents', icon: <DocumentIcon />, path: '/employee/documents' },
    { title: 'Payslips', icon: <PayslipIcon />, path: '/employee/payslips' },
    { title: 'Timesheets', icon: <TimesheetIcon />, path: '/employee/timesheets' },
    { title: 'EU Postings', icon: <PostingIcon />, path: '/employee/postings' },
    { title: 'Settings', icon: <SettingsIcon />, path: '/employee/settings' },
  ];

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: '#1976d2',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Employee Portal
          </Typography>

          <IconButton color="inherit">
            <Badge badgeContent={2} color="error">
              <NotificationIcon />
            </Badge>
          </IconButton>
          
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Side Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            bgcolor: '#fff',
            borderRight: '1px solid #e0e0e0'
          }
        }}
      >
        <Toolbar />
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#1976d2' }}>
              {employeeData?.first_name?.[0]}{employeeData?.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {employeeData?.first_name} {employeeData?.last_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {employeeData?.personal_code}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <List>
          {menuItems.map((item) => (
            <ListItem 
              button 
              key={item.title}
              onClick={() => {
                navigate(item.path);
                setDrawerOpen(false);
              }}
              sx={{
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              <ListItemIcon sx={{ color: '#1976d2' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.title} />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        <Container maxWidth="lg">
          {/* Welcome Section */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff' }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Welcome back, {employeeData?.first_name}!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Here's an overview of your current status and activities.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Chip 
                  label={employeeData?.status === 'active' ? 'Active Employee' : 'Inactive'}
                  color={employeeData?.status === 'active' ? 'success' : 'default'}
                  size="medium"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DocumentIcon sx={{ color: '#1976d2', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Documents
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: stats.documentsExpiring > 0 ? '#d32f2f' : '#1976d2' }}>
                    {stats.documentsExpiring}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Expiring Soon
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PayslipIcon sx={{ color: '#1976d2', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Payslips
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {stats.recentPayslips}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Available
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TimesheetIcon sx={{ color: '#1976d2', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Timesheets
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: stats.pendingTimesheets > 0 ? '#ed6c02' : '#1976d2' }}>
                    {stats.pendingTimesheets}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pending Submission
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WorkIcon sx={{ color: '#1976d2', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Contract
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {stats.activeContract ? '1' : '0'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active Contract
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Current Employment Details */}
          <Grid container spacing={3}>
            {/* Contract Details */}
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: '#fff', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <ContractIcon sx={{ mr: 1, color: '#1976d2' }} />
                    Current Employment
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {stats.activeContract ? (
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Position
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {stats.activeContract.position_title}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Location
                        </Typography>
                        <Typography variant="body1">
                          {stats.activeContract.work_location}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Contract Period
                        </Typography>
                        <Typography variant="body1">
                          {new Date(stats.activeContract.start_date).toLocaleDateString()} - 
                          {stats.activeContract.end_date ? 
                            new Date(stats.activeContract.end_date).toLocaleDateString() : 
                            'Ongoing'}
                        </Typography>
                      </Box>

                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => navigate('/employee/contracts')}
                        sx={{ mt: 2, borderColor: '#1976d2', color: '#1976d2' }}
                      >
                        View Contract Details
                      </Button>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No active contract at the moment
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Posting Details */}
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: '#fff', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationIcon sx={{ mr: 1, color: '#1976d2' }} />
                    EU Posting Status
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {stats.currentPosting ? (
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Country
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {stats.currentPosting.host_country}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Host Company
                        </Typography>
                        <Typography variant="body1">
                          {stats.currentPosting.host_company_name}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          A1 Form
                        </Typography>
                        <Typography variant="body1">
                          {stats.currentPosting.a1_form_number || 'Pending'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Valid Until
                        </Typography>
                        <Typography variant="body1">
                          {new Date(stats.currentPosting.posting_end_date).toLocaleDateString()}
                        </Typography>
                      </Box>

                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => navigate('/employee/postings')}
                        sx={{ mt: 2, borderColor: '#1976d2', color: '#1976d2' }}
                      >
                        View Posting Details
                      </Button>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No active EU posting
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Paper sx={{ p: 3, mt: 3, bgcolor: '#fff' }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<TimesheetIcon />}
                  onClick={() => navigate('/employee/timesheets/new')}
                  sx={{ 
                    bgcolor: '#1976d2',
                    py: 1.5,
                    '&:hover': {
                      bgcolor: '#1565c0'
                    }
                  }}
                >
                  Submit Timesheet
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PayslipIcon />}
                  onClick={() => navigate('/employee/payslips')}
                  sx={{ 
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    py: 1.5
                  }}
                >
                  View Payslips
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DocumentIcon />}
                  onClick={() => navigate('/employee/documents')}
                  sx={{ 
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    py: 1.5
                  }}
                >
                  My Documents
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  onClick={() => navigate('/employee/profile')}
                  sx={{ 
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    py: 1.5
                  }}
                >
                  Update Profile
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default EmployeeDashboard;
